/**
 * pmo#1751 / GH#2107 — `complete-onboarding` con los datos fiscales del alta, SIN que puedan trabar
 * el onboarding.
 *
 * Los datos fiscales se cargan después en Configuración → Facturación si hace falta; el cliente NO
 * puede quedarse sin entrar al panel porque el backend rechazó el bloque `fiscal` (validación,
 * `FISCAL_SOLO_DUENO`…). Por eso, si falla con 400 y el pedido llevaba `fiscal`, se reintenta UNA vez
 * sin él y se deja una marca de «fiscal pendiente». Un 400 que no era del fiscal vuelve a fallar
 * igual sin él y se muestra como siempre. Cualquier otro error (red, 401, 5xx) no se reintenta.
 *
 * Generador puro: recibe la función que llama a la API y el storage, para poder probarlo paso a paso.
 */
import { call } from 'redux-saga/effects';
import {
  borrarFiscalDelAlta,
  marcarFiscalPendiente,
  type FiscalOnboarding,
} from '@/features/onboarding/utils/fiscal-alta';

export const esRechazo400 = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 400;

type ConFiscal = { fiscal?: FiscalOnboarding };

export function* completarOnboardingConFiscal<T extends ConFiscal, R>(
  llamar: (datos: T) => Promise<R>,
  onboardingData: T,
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  /** Almacén que sobrevive a cerrar la pestaña (localStorage), solo para la marca de «fiscal pendiente». */
  duradero?: Pick<Storage, 'setItem'>,
  /** Correo de quien se da de alta: la marca queda atada a él. */
  correo?: string | null,
): Generator<unknown, R, R> {
  try {
    const respuesta = (yield call(llamar, onboardingData)) as R;
    borrarFiscalDelAlta(storage);
    return respuesta;
  } catch (err) {
    if (!onboardingData.fiscal || !esRechazo400(err)) throw err;
    // eslint-disable-next-line no-console
    console.warn('[Onboarding] el backend rechazó los datos fiscales; se completa sin ellos');
    const { fiscal: _omitido, ...sinFiscal } = onboardingData;
    const respuesta = (yield call(llamar, sinFiscal as unknown as T)) as R;
    marcarFiscalPendiente(correo, ...(duradero ? [storage, duradero] : [storage]));
    borrarFiscalDelAlta(storage);
    return respuesta;
  }
}
